import { LitElement, html, nothing } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollSmoother } from 'gsap/ScrollSmoother';

gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

import '@awesome.me/webawesome/dist/styles/webawesome.css';
import '@awesome.me/webawesome/dist/components/page/page.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/icon/icon.js';

import './pages/app-home/app-home';
import './components/header';
import './components/footer';

import { resolveRouterPath, router } from './router';
import { appIndexStyles } from './app-index.styles';

const homePath = resolveRouterPath();
const aboutPath = resolveRouterPath('about');
const contactPath = resolveRouterPath('contact');
const ragPath = resolveRouterPath('rag');

type StatusSeverity = 'status' | 'alert';

@customElement('app-index')
export class AppIndex extends LitElement {
  static styles = [appIndexStyles];

  @state() private currentPath = window.location.pathname;
  @state() private statusMessage = '';
  @state() private statusSeverity: StatusSeverity = 'status';
  @state() private updateRegistration?: ServiceWorkerRegistration;

  @query('#smooth-wrapper') private smoothWrapper!: HTMLElement;
  @query('#smooth-content') private smoothContent!: HTMLElement;
  @query('#navigation') private navigation!: HTMLElement;

  private statusDismissed = false;
  private updateActivationRequested = false;
  private registration?: ServiceWorkerRegistration;
  private smoothMedia?: gsap.MatchMedia;
  private navigationMedia?: gsap.MatchMedia;
  private scrollSmoother?: ScrollSmoother;

  firstUpdated(): void {
    this.removeUpdateMarker();
    router.addEventListener('route-changed', this.handleRouteChange);
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
    navigator.serviceWorker?.addEventListener(
      'controllerchange',
      this.handleControllerChange
    );
    navigator.serviceWorker?.addEventListener('message', this.handleServiceWorkerMessage);

    if (!navigator.onLine) {
      this.setStatus(
        'You are offline. Previously visited content may still be available.',
        'alert'
      );
    }

    this.initializeSmoothScrolling();
    this.initializeDesktopNavigation();
    void this.registerServiceWorker();
  }

  disconnectedCallback(): void {
    router.removeEventListener('route-changed', this.handleRouteChange);
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    navigator.serviceWorker?.removeEventListener(
      'controllerchange',
      this.handleControllerChange
    );
    navigator.serviceWorker?.removeEventListener(
      'message',
      this.handleServiceWorkerMessage
    );
    this.smoothMedia?.revert();
    this.navigationMedia?.revert();
    super.disconnectedCallback();
  }

  private readonly handleRouteChange = (): void => {
    this.currentPath = window.location.pathname;
    this.requestUpdate();

    this.updateComplete.then(() => {
      this.scrollSmoother?.scrollTop(0) ?? window.scrollTo(0, 0);
      requestAnimationFrame(() => ScrollTrigger.refresh());
      // Route content changes without a document navigation, so move focus to
      // the landmark to give keyboard and screen-reader users clear context.
      this.renderRoot.querySelector<HTMLElement>('#routed-content')?.focus();
    });
  };

  private initializeSmoothScrolling(): void {
    this.smoothMedia = gsap.matchMedia();
    this.smoothMedia.add('(prefers-reduced-motion: no-preference)', () => {
      // These element references are required because IDs in a Lit shadow root
      // are not discoverable by ScrollSmoother's document-level selectors.
      const smoother = ScrollSmoother.create({
        wrapper: this.smoothWrapper,
        content: this.smoothContent,
        smooth: 1,
        effects: true,
      });
      this.scrollSmoother = smoother;

      return () => {
        smoother.kill();
        this.scrollSmoother = undefined;
      };
    });
  }

  private initializeDesktopNavigation(): void {
    this.navigationMedia = gsap.matchMedia();
    this.navigationMedia.add(
      {
        desktop: '(min-width: 769px)',
        reducedMotion: '(prefers-reduced-motion: reduce)',
      },
      (context) => {
        const { desktop, reducedMotion } = context.conditions as {
          desktop: boolean;
          reducedMotion: boolean;
        };

        if (!desktop || reducedMotion) {
          return;
        }

        // Move only with transforms so the sidebar follows the document without
        // forcing layout recalculation on every scroll event.
        const moveTo = gsap.quickTo(this.navigation, 'y', {
          duration: 0.4,
          ease: 'power3.out',
        });
        const trigger = ScrollTrigger.create({
          start: 0,
          end: 'max',
          onUpdate: (self) => moveTo(self.scroll()),
        });

        return () => {
          trigger.kill();
          gsap.set(this.navigation, { y: 0 });
        };
      }
    );
  }

  private readonly handleOnline = (): void => {
    this.setStatus('Connection restored. You are back online.', 'status');
  };

  private readonly handleOffline = (): void => {
    this.setStatus(
      'You are offline. Previously visited content may still be available.',
      'alert'
    );
  };

  private readonly handleControllerChange = (): void => {
    this.completeUpdateReload();
  };

  private readonly handleServiceWorkerMessage = (event: MessageEvent): void => {
    if (
      this.updateActivationRequested &&
      event.data?.type === 'SKIP_WAITING_ACCEPTED'
    ) {
      this.completeUpdateReload();
    }
  };

  private completeUpdateReload(): void {
    if (!this.updateActivationRequested) {
      return;
    }

    this.updateActivationRequested = false;
    // The unique query bypasses SPA navigation so the browser loads the new worker's shell.
    const updateUrl = new URL(window.location.href);
    updateUrl.searchParams.set('pwa-update', Date.now().toString());
    window.location.assign(updateUrl);
  }

  private removeUpdateMarker(): void {
    const currentUrl = new URL(window.location.href);
    if (!currentUrl.searchParams.has('pwa-update')) {
      return;
    }

    currentUrl.searchParams.delete('pwa-update');
    // Keep the clean URL without adding an extra Back-button history entry.
    window.history.replaceState(null, '', currentUrl);
  }

  private async registerServiceWorker(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    try {
      this.registration = await navigator.serviceWorker.register('/sw.js');
      this.watchForUpdate(this.registration);

      if (this.registration.waiting) {
        this.announceUpdate(this.registration);
      }
    } catch (error) {
      console.error('Service worker registration failed.', error);
    }
  }

  private watchForUpdate(registration: ServiceWorkerRegistration): void {
    registration.addEventListener('updatefound', () => {
      const installing = registration.installing;
      if (!installing) {
        return;
      }

      installing.addEventListener('statechange', () => {
        if (
          installing.state === 'installed' &&
          navigator.serviceWorker.controller
        ) {
          this.announceUpdate(registration);
        }
      });
    });
  }

  private announceUpdate(registration: ServiceWorkerRegistration): void {
    if (this.statusDismissed) {
      return;
    }

    this.updateRegistration = registration;
    this.setStatus(
      'An app update is ready. Reload when you are ready to use the latest version.',
      'status'
    );
  }

  private setStatus(message: string, severity: StatusSeverity): void {
    this.statusDismissed = false;
    this.statusMessage = message;
    this.statusSeverity = severity;
  }

  private reloadForUpdate(): void {
    const waitingWorker = this.updateRegistration?.waiting;
    if (!waitingWorker) {
      return;
    }

    this.updateActivationRequested = true;
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    this.completeUpdateReload();
  }

  private dismissStatus(): void {
    this.statusDismissed = true;
    this.statusMessage = '';
    this.updateRegistration = undefined;
  }

  private isCurrentPath(path: string): boolean {
    return this.currentPath === path;
  }

  render() {
    return html`
      <div id="smooth-wrapper">
        <div id="smooth-content">
          <wa-page mobile-breakpoint="768">
            <!-- wa-page owns the skip-link target and keyboard behavior; this slot changes only its label. -->
            <span slot="skip-to-content">Skip to content</span>

            <app-header slot="header">
              <wa-button
                slot="navigation-toggle"
                data-toggle-nav
                appearance="plain"
                aria-label="Open navigation"
              >
                <wa-icon name="bars" aria-hidden="true"></wa-icon>
              </wa-button>
            </app-header>
            <nav
              id="navigation"
              slot="navigation"
              class="wa-stack wa-gap-2xs"
              aria-label="Main navigation"
            >
              <a
                href="${homePath}"
                data-drawer="close"
                aria-current=${this.isCurrentPath(homePath) ? 'page' : nothing}
              >Home</a>
              <a
                href="${aboutPath}"
                data-drawer="close"
                aria-current=${this.isCurrentPath(aboutPath) ? 'page' : nothing}
              >About</a>
              <a
                href="${contactPath}"
                data-drawer="close"
                aria-current=${this.isCurrentPath(contactPath) ? 'page' : nothing}
              >Contact</a>
              <a
                href="${ragPath}"
                data-drawer="close"
                aria-current=${this.isCurrentPath(ragPath) ? 'page' : nothing}
              >RAG Demo</a>
            </nav>

            <main id="routed-content" tabindex="-1">${router.render()}</main>
            <app-footer slot="footer"></app-footer>
          </wa-page>
        </div>
      </div>
      <div
        id="network-status"
        role=${this.statusSeverity}
        aria-live=${this.statusSeverity === 'alert' ? 'assertive' : 'polite'}
        data-severity=${this.statusSeverity}
        ?hidden=${!this.statusMessage}
      >
        <span>${this.statusMessage}</span>
        ${this.updateRegistration
          ? html`<wa-button @click=${this.reloadForUpdate}>Reload now</wa-button>`
          : nothing}
        <wa-button appearance="plain" @click=${this.dismissStatus}>
          Dismiss
        </wa-button>
      </div>
      <nav class="mobile-bottom-nav" aria-label="Mobile navigation">
        <a
          href="${homePath}"
          aria-current=${this.isCurrentPath(homePath) ? 'page' : nothing}
        >
          <wa-icon name="house" aria-hidden="true"></wa-icon>
          <span>Home</span>
        </a>
        <a
          href="${aboutPath}"
          aria-current=${this.isCurrentPath(aboutPath) ? 'page' : nothing}
        >
          <wa-icon name="circle-info" aria-hidden="true"></wa-icon>
          <span>About</span>
        </a>
        <a
          href="${contactPath}"
          aria-current=${this.isCurrentPath(contactPath) ? 'page' : nothing}
        >
          <wa-icon name="envelope" aria-hidden="true"></wa-icon>
          <span>Contact</span>
        </a>
        <a
          href="${ragPath}"
          aria-current=${this.isCurrentPath(ragPath) ? 'page' : nothing}
        >
          <wa-icon name="comments" aria-hidden="true"></wa-icon>
          <span>RAG</span>
        </a>
      </nav>
    `;
  }
}
